// src/components/InpatientDoctorMatrix.tsx (2026-08-03 新設)
// 入院実績(医師別) タブの「医師別マトリックス」。科別マトリックスの直下に置く。
//
// 【データ源】/api/query/doctor_matrix = fact_disease_month (= 科別マトリックスが使う
//   fact_long_all と同じ disease_day 由来)。2026-06 実測で 入院収入/GP/在院・入院患者延数/
//   退院患者数/予定入院数/DPC期間2以内・超 が科別と 0 差で一致する。
//
// 【科別との違い (医師軸に無いもの)】
//   - 予算が無い (fact_budget_all は科まで) → 達成度 A/B/C・予算比 の列は出さない。
//     色分けトグルが「予算比」でも、この表だけは前年度比で色分けする (注記を出す)。
//   - 手術麻酔収入 / 手術室件数 / 紹介入院率 / 常勤医師数 は医師軸のデータが無いので列にしない。
//   - 「新規受持数」は 新規入院 + 他医師からの受持替え (dr_in)。科別の「他科から転科」に相当する
//     受持替えを医師軸で数えたもので、平均在院日数の分母も同じ定義で揃えてある。
//
// 【診療科の扱い】admdrcd は「その在院日の主治医」なので、1 人の医師が複数科に跨りうる
//   (2026-04〜06 実測: 106 人中 14 人)。行は 医師 × 在院診療科 で取得し、
//   - 科フォーカスあり → その科の行だけ (= 科別マトリックスのその 1 行を医師に分解した形)
//   - 科フォーカスなし → 医師で合算し、主たる科 (在院患者延数 最大) を診療科列に出す
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters } from "../store";
import { fmtYenScaled, isYenCardMetric, yenUseOku, fmtByUnitValueOnly, fmtRatioPct } from "../lib/format";
import { colorForYoy } from "./KpiBigPanel";
import { BUDGET_TAB_SPECS, COMPARE_EXTRA_SPECS, type MetricSpec } from "./prototypes/budgetSpec";
import { RATIO_METRICS, weightedRatio } from "../lib/ratioMetrics";
import { cellBg } from "./InpatientBudgetComparePanel";
import type { DoctorMatrixResponse, Direction } from "@shared/api";

const ALL_SPECS: MetricSpec[] = [...BUDGET_TAB_SPECS, ...COMPARE_EXTRA_SPECS];
const specOf = (key: string): MetricSpec | undefined => ALL_SPECS.find((s) => s.key === key);

// 列順 = 科別マトリックス準拠 (収入 → 前年比 → 延数 → 単価 → 在院日数 → 受持 → 予定 → DPCⅡ)。
// 収入/延数/単価はサイドバーの基準トグルに連動 (科別・カードと同じ解決)。
function doctorMatrixColKeys(revenueBase: "入院収入" | "粗利", volumeBase: "入院" | "在院"): string[] {
  const gp = revenueBase === "粗利";
  const zaiin = volumeBase === "在院";
  const revenue = gp ? "GP" : "入院収入";
  const volume = zaiin ? "在院患者延数" : "入院患者延数";
  const unit = gp ? (zaiin ? "GP単価_在院" : "GP単価") : (zaiin ? "在院単価" : "入院単価");
  return [revenue, "前年比", volume, unit, "平均在院日数", "新規受持数", "予定入院数", "DPC期間2以内率"];
}

// ★「平均在院日数」を医師別でそのまま名乗らせない (2026-08-03 Codex 敵対レビュー指摘)。
//   分母に医師交代 (dr_in/dr_out) が入るので、値は「1 人の医師が 1 人の患者を持っていた平均日数」。
//   実測 2026-06 病院全体: 医師式 9.72 日 vs 科別マトリックスの平均在院日数 11.7 日。
//   同じ画面で上下に並ぶので、同名にすると「医師の方が短い = 早く退院させている」と誤読される。
const LABEL_OVERRIDE: Record<string, string> = {
  "平均在院日数": "平均受持日数",
  "DPC期間2以内率": "DPCⅡ以内率",
};

type Field = "actual" | "prevYear";
type ColMeta = {
  key: string; label: string; unitLabel: string; direction: Direction;
  special?: "yoyRatio";
  fmt: (v: number | null) => string;
};
type DoctorRow = {
  admdrcd: string;
  医師名: string;
  診療科: string;          // 科フォーカス中はその科 / 未フォーカスは主たる科
  nDepts: number;          // 期間内に在院日を持った科の数 (2 以上なら診療科列に注記)
  cells: ({ actual: number | null; prevYear: number | null } | null)[];
  revActual: number | null;
  revPrevYear: number | null;
};

export function InpatientDoctorMatrix({
  fy, selectedMonths, focusedDept, revenueBase, volumeBase, rangeLabel, condRows, topN, colorMode,
}: {
  fy: number;
  selectedMonths: Set<string>;
  focusedDept: string | null;
  revenueBase: "入院収入" | "粗利";
  volumeBase: "入院" | "在院";
  rangeLabel: string;
  condRows: Record<string, unknown>[];
  topN: number;
  colorMode: "yoy" | "budget";
}) {
  const f = useFilters();
  const { data, isFetching, error } = useQuery<DoctorMatrixResponse>({
    queryKey: ["doctor_matrix", f.granularity, f.iryo, f.yotei, fy],
    queryFn: () => api.doctorMatrix({
      granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
      fyStart: `${fy}-04-01`, fyEnd: `${fy + 1}-03-01`,
    }),
    placeholderData: (prev) => prev,
  });
  const cols = useMemo(() => doctorMatrixColKeys(revenueBase, volumeBase), [revenueBase, volumeBase]);
  const revenueKey = cols[0];
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "__revenue__", dir: "desc" });

  const { rows, colMeta, nDoctorsAll } = useMemo(() => {
    if (!data) return { rows: [] as DoctorRow[], colMeta: [] as ColMeta[], nDoctorsAll: 0 };
    const nMonths = Math.max(1, selectedMonths.size);

    // ── 期間内の Σ を 医師 (× 科フォーカス) 単位に畳む ──
    //   ★加算指標の月平均は Σ / 選択月数 で出す (欠測月 = 実績ゼロ)。
    //     科別マトリックスは「値のある月だけの平均」だが、医師は月によって在院患者が
    //     居ないことが普通にあるため、非 null 月だけで割ると Σ医師 ≠ 科の値 になる。
    //   ★「値のある月が 1 つも無い」と「合計 0」は区別する (nA/nP で数える)。
    //     医師の入替は普通に起きる (実測: 整形外科は 2026 年度に 5/8 名が前年度実績なし)。
    //     ここを 0 に丸めると「前年度 0 円で診療していた」という嘘になる → 表示は「—」。
    const sums = new Map<string, { a: number; p: number; nA: number; nP: number }>();  // `${admdrcd}|${metric}`
    const names = new Map<string, string>();
    const deptZaiin = new Map<string, Map<string, number>>();   // admdrcd → 科 → Σ在院患者延数
    for (const r of data.rows) {
      if (focusedDept && r.診療科 !== focusedDept) continue;
      names.set(r.admdrcd, r.医師名);
      let a = 0, p = 0, nA = 0, nP = 0;
      for (const t of r.trend) {
        if (!selectedMonths.has(t.date)) continue;
        if (t.actual != null)   { a += t.actual;   nA++; }
        if (t.prevYear != null) { p += t.prevYear; nP++; }
      }
      const k = `${r.admdrcd}|${r.集計区分}`;
      const cur = sums.get(k);
      if (cur) { cur.a += a; cur.p += p; cur.nA += nA; cur.nP += nP; } else { sums.set(k, { a, p, nA, nP }); }
      if (r.集計区分 === "在院患者延数") {
        if (!deptZaiin.has(r.admdrcd)) deptZaiin.set(r.admdrcd, new Map());
        deptZaiin.get(r.admdrcd)!.set(r.診療科, (deptZaiin.get(r.admdrcd)!.get(r.診療科) ?? 0) + a);
      }
    }
    const sumOf = (dr: string, metric: string, field: Field): number | null => {
      const s = sums.get(`${dr}|${metric}`);
      if (!s) return null;
      if (field === "actual") return s.nA > 0 ? s.a : null;
      return s.nP > 0 ? s.p : null;
    };
    // 期間値: 比指標は Σ分子/Σ分母 (加重・科別/カードと同じ RATIO_METRICS)、加算指標は Σ/選択月数
    const periodValue = (dr: string, key: string, field: Field): number | null => {
      const spec = RATIO_METRICS[key];
      if (spec && !spec.unitDays) return weightedRatio(spec, (m) => sumOf(dr, m, field));
      const s = sumOf(dr, key, field);
      return s == null ? null : s / nMonths;
    };

    const doctors = Array.from(names.keys());
    // 列メタ: 円系は列内 |max| で 億/万円 スケールを列単位に固定 (行間で桁を揃える)
    const noFmt = (v: number | null) => v == null ? "—" : String(v);
    const colMeta: ColMeta[] = cols.map((key) => {
      if (key === "前年比") {
        return { key, label: "前年比", unitLabel: "%", direction: "neutral" as Direction, special: "yoyRatio" as const, fmt: noFmt };
      }
      const spec = specOf(key);
      const unit = spec?.unit ?? "";
      const direction: Direction = spec?.direction ?? "neutral";
      const label = LABEL_OVERRIDE[key] ?? spec?.label ?? key;
      if (isYenCardMetric(key, unit)) {
        let maxAbs = 0;
        for (const dr of doctors) {
          for (const fld of ["actual", "prevYear"] as Field[]) {
            const v = periodValue(dr, key, fld);
            if (v != null) maxAbs = Math.max(maxAbs, Math.abs(v));
          }
        }
        const oku = yenUseOku(maxAbs);
        return { key, label, unitLabel: oku ? "億円" : "万円", direction,
                 fmt: (v: number | null) => v == null ? "—" : fmtYenScaled(v, oku).value };
      }
      return { key, label, unitLabel: unit, direction,
               fmt: (v: number | null) => v == null ? "—" : fmtByUnitValueOnly(v, unit) };
    });

    const rowOf = (dr: string): DoctorRow => {
      const byDept = deptZaiin.get(dr);
      const main = byDept
        ? Array.from(byDept.entries()).sort((x, y) => y[1] - x[1])[0]?.[0] ?? "—"
        : "—";
      return {
        admdrcd: dr,
        医師名: names.get(dr) ?? "(不明)",
        診療科: focusedDept ?? main,
        nDepts: byDept ? byDept.size : 0,
        cells: cols.map((k) => k === "前年比" ? null : ({
          actual: periodValue(dr, k, "actual"),
          prevYear: periodValue(dr, k, "prevYear"),
        })),
        revActual: periodValue(dr, revenueKey, "actual"),
        revPrevYear: periodValue(dr, revenueKey, "prevYear"),
      };
    };
    // 期間内に在院患者が居ない医師 (退職・異動・前年だけ在籍) は行にしない。
    //   前年値だけの行を残すと、実績 0 の行が並んで印刷 1 枚を食う (整形外科で 13 名中 5 名が該当)。
    const all = doctors
      .filter((dr) => (sumOf(dr, "入院患者延数", "actual") ?? 0) > 0)
      .map(rowOf);

    // ソート: 既定 = 収入列の実績 降順 (= 「上位 N 名」の N)。null は末尾・医師名で安定化
    const sortKey = sort.key === "__revenue__" ? revenueKey : sort.key;
    const sVal = (r: DoctorRow): number | null => {
      if (sortKey === "前年比") {
        return r.revActual != null && r.revPrevYear ? r.revActual / r.revPrevYear : null;
      }
      const j = cols.indexOf(sortKey);
      return j >= 0 ? (r.cells[j]?.actual ?? null) : r.revActual;
    };
    all.sort((x, y) => {
      const vx = sVal(x), vy = sVal(y);
      if (vx == null && vy == null) return x.医師名.localeCompare(y.医師名, "ja");
      if (vx == null) return 1;
      if (vy == null) return -1;
      if (vx === vy) return x.医師名.localeCompare(y.医師名, "ja");
      return sort.dir === "desc" ? vy - vx : vx - vy;
    });
    return { rows: all, colMeta, nDoctorsAll: all.length };
  }, [data, selectedMonths, cols, sort, focusedDept, revenueKey]);

  // 表示は上位 N 名まで (N = サイドバーの 20/25/30)。これは「印刷 1 枚に収める」ための上限。
  //   ★科フォーカス中は「科 1 行 + 医師別」を A4 横 1 枚に収める制約があるので、さらに厳しい上限。
  //     実測 (印刷メディアでの計測): 1 行 21.7px・科カード+見出し+脚注で 195px・A4 横の印刷領域 756px
  //     → 収まるのは (756-195)/21.7 ≒ 25 行。30 行だと 846px で溢れる (Codex 指摘を実測で確認)。
  //   現データでは在院日のある医師は最多 17 名 (救急科・年度全体) なので通常この上限には当たらない。
  const COMBINED_PRINT_MAX_ROWS = 25;
  const effectiveTopN = focusedDept ? Math.min(topN, COMBINED_PRINT_MAX_ROWS) : topN;
  const shown = rows.slice(0, effectiveTopN);
  const truncated = rows.length > shown.length;
  const onSort = (key: string) =>
    setSort((s) => s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" });
  const effectiveSortKey = sort.key === "__revenue__" ? revenueKey : sort.key;

  // xlsx 出力: 表示ソート順・**全医師** (TOP N で切らない)。値は fact 生値 (千円/円/件/人/日・%系は×100)。
  const exportXlsx = async () => {
    const pct = (v: number | null) => v == null ? null : Math.round(v * 1000) / 10;
    const xrows = rows.map((r) => {
      const o: Record<string, unknown> = { 医師名: r.医師名, 診療科: r.診療科 };
      o["前年比(%)"] = pct(r.revActual != null && r.revPrevYear ? r.revActual / r.revPrevYear : null);
      colMeta.forEach((cm, j) => {
        if (cm.special) return;
        const rawUnit = specOf(cm.key)?.unit ?? "";
        const isPct = rawUnit === "%";
        const suffix = isPct ? "(%)" : rawUnit ? `(${rawUnit})` : "";
        const conv = (v: number | null) => v == null ? null : isPct ? pct(v) : v;
        const c = r.cells[j];
        o[`${cm.label}_実績${suffix}`] = conv(c?.actual ?? null);
        o[`${cm.label}_前年度${suffix}`] = conv(c?.prevYear ?? null);
      });
      return o;
    });
    const { downloadXlsxSheets } = await import("../lib/xlsx");
    await downloadXlsxSheets([
      { name: "医師別マトリックス", rows: xrows },
      { name: "出力条件", rows: [
        { 項目: "出力元", 値: "入院実績(医師別) 医師別マトリックス" },
        { 項目: "対象", 値: focusedDept ? `${focusedDept} の医師 (全員)` : "全医師 (画面は上位のみ・本xlsxは全員)" },
        { 項目: "医師の帰属", 値: "その在院日の主治医 (日次)。1名が複数科に跨る場合あり" },
        { 項目: "予算", 値: "医師別の予算は存在しないため 前年度比 のみ" },
        ...condRows,
      ] },
    ], `医師別マトリックス_${fy}年度_${rangeLabel}`);
  };

  return (
    <section className="card p-3 doctor-matrix-sec">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h3 className="text-base font-semibold">医師別マトリックス</h3>
        <span className="text-[11px] text-slate-500">
          {focusedDept
            ? <>🎯 <b>{focusedDept}</b> の医師 {truncated ? <>(上位 {shown.length} 名 / 全 {nDoctorsAll} 名)</> : <>(全 {nDoctorsAll} 名)</>}</>
            : <>上位 {shown.length} 名 / 全 {nDoctorsAll} 名</>}
          {" "}× 主要指標 (期間 {rangeLabel} の月平均)。上段=今年度、下段=(前年度)。
        </span>
        {/* placeholderData で前条件の表を残すので、更新中は isFetching で明示する
            (isLoading だと再取得中は false になり「新しい条件の見出し + 古い数字」に見える) */}
        {isFetching && <span className="text-[11px] text-amber-600">条件変更中…</span>}
        <button type="button" onClick={exportXlsx}
                className="ml-auto px-3 py-1 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 no-print">
          ⬇ xlsx
        </button>
      </div>
      {truncated && (
        <div className="text-[10px] text-amber-700 mb-1 leading-tight">
          ★上位 {shown.length} 名だけを表示しています (全 {nDoctorsAll} 名)。
          この表の合計は{focusedDept ? "上の科別の行" : "病院全体"}と一致しません。全員を出すには行数を増やすか、xlsx (全員分) を使ってください。
        </div>
      )}
      {colorMode === "budget" && (
        <div className="text-[10px] text-amber-700 mb-1 leading-tight">
          医師別に予算はありません。この表だけ前年度比で色分けしています。
        </div>
      )}
      {error && (
        <div className="text-xs text-red-700 bg-red-50 rounded p-2">
          医師別データ取得エラー: {(error as Error).message}
        </div>
      )}
      <div className="overflow-x-auto text-xs">
        <table className="w-full border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr>
              <th className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20 w-32">医師</th>
              {!focusedDept && <th className="text-left px-2 py-1 border-b border-slate-200 w-28">診療科</th>}
              {colMeta.map((cm) => (
                <th key={cm.key}
                    className="text-right px-1.5 py-1 border-b border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                    onClick={() => onSort(cm.key)}
                    title="クリックで並べ替え (昇順/降順)">
                  <div className="leading-tight">
                    {cm.label}
                    {effectiveSortKey === cm.key && <span className="ml-0.5">{sort.dir === "desc" ? "▼" : "▲"}</span>}
                  </div>
                  <div className="font-normal text-[10px] text-slate-500">{cm.unitLabel}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row, i) => {
              const zebra = i % 2 === 1 ? "bg-slate-50" : "bg-white";
              return (
                <tr key={row.admdrcd} className={`border-b border-slate-100 hover:bg-blue-50/40 ${zebra}`}>
                  <td className={`text-left px-2 py-0.5 sticky left-0 z-[1] ${zebra}`}>{row.医師名}</td>
                  {!focusedDept && (
                    <td className="text-left px-2 py-0.5 text-slate-500">
                      {row.診療科}{row.nDepts > 1 && <span className="text-slate-400"> 他{row.nDepts - 1}科</span>}
                    </td>
                  )}
                  {row.cells.map((cell, j) => {
                    const cm = colMeta[j];
                    if (cm.special === "yoyRatio") {
                      return (
                        <td key={cm.key} className="text-right px-1.5 py-0.5 tabular-nums leading-tight">
                          <span className={`font-bold ${colorForYoy(row.revActual, row.revPrevYear, "higher_is_better")}`}>
                            {fmtRatioPct(row.revActual, row.revPrevYear)}
                          </span>
                        </td>
                      );
                    }
                    const actual = cell?.actual ?? null;
                    const ref = cell?.prevYear ?? null;   // 医師別は予算が無いので常に前年度
                    const pct = actual != null && ref != null && ref !== 0 ? (actual - ref) / Math.abs(ref) : null;
                    const bg = cellBg(pct, cm.direction);
                    return (
                      <td key={cm.key} className={`text-right px-1.5 py-0.5 tabular-nums leading-tight ${bg}`}>
                        <div>{cm.fmt(actual)}</div>
                        <div className="text-slate-500">({cm.fmt(ref)})</div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {!isFetching && shown.length === 0 && (
              <tr><td colSpan={colMeta.length + (focusedDept ? 1 : 2)} className="text-center text-slate-400 py-3">
                この条件に該当する医師の実績がありません
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-400 mt-1 leading-tight">
        医師 = その在院日の主治医 (日次)。期間内に在院患者が居た医師のみ (前年だけ在籍した医師は出しません)。
        新規受持数 = 新規入院 + 他医師からの受持替え。
        医師別に予算・手術麻酔収入・手術室件数・紹介入院率のデータは無いため列にしていません。
        ★<b>平均受持日数は科別の「平均在院日数」とは別物</b>: 分母に医師の受持替えが入るため、
        1 人の医師が 1 人の患者を持っていた平均日数になります (2026-06 病院全体で 9.7 日 vs 科別 11.7 日)。
        上下に並ぶ 2 表で数字を比べないでください。
        ★<b>新規受持数・予定入院数は科別マトリックスの値と一致しません</b>: 科別は入院日の科帰属を
        公式の新規入院データで補正していますが、医師別は日次の主治医で数えるためです
        (2026-04〜06 実測の科別差: 予定入院数 最大 2 名 / 新規入院数 は救急科で最大 22 名)。
        収入・粗利・在院/入院患者延数・退院患者数・DPCⅡ以内率は科別と一致します。
        {!focusedDept && " 科別マトリックスの行をクリックすると、その科の医師だけに絞り込めます。"}
      </div>
    </section>
  );
}
